import * as React from "react";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import {
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";

import { CustomDialog, CustomDialogTitle } from "../../ui";
import { editPromptSchema } from "../../schema";
import { useUpdatePromptAnswerMutation } from "../../services/profileAPI";

type AnswerPromptsDialogPropsType = {
  open: boolean;
  onClose: () => void;
  question: string;
  answer: string;
  questionId: string;
};

const AnswerPromptsDialog = ({
  open = false,
  onClose = () => {},
  question,
  answer,
  questionId,
}: AnswerPromptsDialogPropsType) => {
  const { enqueueSnackbar } = useSnackbar();
  const [updatePromptAnswerMutation] = useUpdatePromptAnswerMutation();

  const formik = useFormik({
    initialValues: {
      answer: answer || "",
    },
    validationSchema: editPromptSchema,
    onSubmit: async (values) => {
      try {
        const requestBody = {
          questionId: questionId,
          text: values?.answer?.trim(),
        };
        const response = await updatePromptAnswerMutation(requestBody).unwrap();
        if (response) {
          enqueueSnackbar("prompt updated successfully", {
            variant: "success",
          });
          onClose();
        }
      } catch (error: any) {
        const errorMessage =
          error?.data?.message ||
          "an error occured while adding the prompt. please try again later";
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    },
  });

  return (
    <CustomDialog open={open} onClose={onClose} fullWidth>
      <CustomDialogTitle onClose={onClose}>
        <Typography>edit prompt</Typography>
      </CustomDialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Typography color="text.secondary" fontSize={14}>
            {question}
          </Typography>
          <TextField
            name="answer"
            placeholder="enter your answer here"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formik.values.answer}
            onChange={formik.handleChange}
            error={
              Boolean(formik.touched.answer) && Boolean(formik.errors.answer)
            }
            helperText={formik.touched.answer && formik.errors.answer}
            sx={{ mt: 2, mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <LoadingButton
            variant="text"
            type="submit"
            loading={formik.isSubmitting}
          >
            update
          </LoadingButton>
        </DialogActions>
      </form>
    </CustomDialog>
  );
};

export default AnswerPromptsDialog;
